import { addTableRows } from '@/store/thunks/UpdateTable';
import {
  AnchorButton,
  InlineChipWidget,
  LinkedMenuItemText,
  SIZE,
  StyledDescription,
  TYPE,
} from '@/components/widgets/InlineChipWidget';
import { ChatUIContext } from '@/components/context/ChatUIContext';
import { EventType } from '@/models/Interaction';
import { getChatAPI, isCorpusIdSearchable } from '@/api/ChatApi';
import { getCurrentThread } from '@/store/reducers/UIState';
import {
  mkPaperChipTrackAttrs,
  mkPaperMenuOpenLitReviewBtnTrackAttrs,
  mkPaperMenuOpenReaderBtnTrackAttrs,
  mkPaperMenuOpenS2BtnTrackAttrs,
  mkPaperMenuPaperQABtnTrackAttrs,
  mkPaperMenuPaperTitleTrackAttrs,
} from '@/analytics/props/inlineWidget';
import { openReaderWidget } from '@/store/actionCreators/WidgetActions';
import { optAppConfig } from '@/utils/config';
import { PermissionsContext } from '@/components/context/PermissionsContext';
import { TABLE_ANNOTATION } from 'configs/NoraVariants';
import { TableToolPaper } from '@/actions/ProcessUIToolCalls';
import { TableViewMode } from '@/components/table/TableView';
import { useAppDispatch, useAppSelector } from '@/store/hooks';

import { Divider, Menu, MenuItem, styled } from '@mui/material';
import { FC, useCallback, useContext, useEffect, useState } from 'react';
import ArticleIcon from '@mui/icons-material/Article';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const noraExperienceVariant = optAppConfig((_) => _.experience.variant);
export interface InlinePaperChipWidgetProps {
  corpusId: number;
  paperTitle: string;
  isMultiLine?: boolean;
  isFullWidth?: boolean;
  hasMargin?: boolean;
  isDarkMode?: boolean;
  hideAddPaperToTable?: boolean;
  warnIfNotSearchable?: boolean;
  size?: SIZE;
  icon?: boolean;
  mode?: TableViewMode;
  children?: React.ReactNode;
}

export const InlinePaperChipWidget: FC<InlinePaperChipWidgetProps> = (
  props: InlinePaperChipWidgetProps,
) => {
  const {
    corpusId,
    paperTitle,
    isMultiLine,
    isFullWidth = false,
    hasMargin = false,
    isDarkMode,
    hideAddPaperToTable = false,
    warnIfNotSearchable = false,
    size = SIZE.medium,
    icon = true,
    mode,
  } = props;
  const dispatch = useAppDispatch();
  const chatApi = getChatAPI();
  const currentThread = useAppSelector(getCurrentThread);
  const { setSuggestedChatMessage } = useContext(ChatUIContext);
  const { isReadOnly } = useContext(PermissionsContext);
  const [isSearchable, setIsSearchable] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAskQuestion = useCallback(() => {
    const defaultQuestion = 'what are its main contributions?';
    const suggestion = `For the paper titled "${paperTitle}", ${defaultQuestion}`;
    setSuggestedChatMessage(
      suggestion,
      suggestion.length - defaultQuestion.length,
      suggestion.length - 1,
    );
    setAnchorEl(null);
  }, [setSuggestedChatMessage, paperTitle]);

  const handleAddToTable = useCallback(() => {
    const paper: TableToolPaper = { corpusId, title: paperTitle };
    const currentThreadId = currentThread?.id;
    if (currentThreadId) {
      dispatch(addTableRows({ currentThreadId, papers: [paper] }));
      if (mode === TableViewMode.RELATED_PAPERS) {
        chatApi.trackEvent({
          type: EventType.WIDGET_TABLE_ADD_RELATED_PAPERS,
          threadId: currentThread.id,
          data: {
            table_id: currentThread?.id,
            corpus_id: corpusId,
          },
        });
      }
    }
    setAnchorEl(null);
  }, [currentThread?.id, corpusId, dispatch]);

  const handleOpenReader = useCallback((): void => {
    if (!currentThread) {
      return;
    }
    dispatch(openReaderWidget({ currentThreadId: currentThread.id, corpusId }));

    setAnchorEl(null);
  }, [corpusId, currentThread, dispatch]);

  useEffect(() => {
    isCorpusIdSearchable(corpusId).then((isSearchable) => {
      setIsSearchable(isSearchable);
    });
  }, [corpusId]);

  return (
    <TitleChipContainer
      isMultiLine={!!isMultiLine}
      isFullWidth={!!isFullWidth}
      hasMargin={hasMargin}
    >
      <AnchorButton onClick={handleClick}>
        <InlineChipWidget
          label={
            <>
              {paperTitle}
              {warnIfNotSearchable && !isSearchable && (
                <WarningChip isDarkMode={isDarkMode}>Abstract Only</WarningChip>
              )}
            </>
          }
          type={TYPE.default}
          icon={
            icon ? isSearchable ? <PictureAsPdfIcon /> : <ArticleIcon /> : false
          }
          size={size}
          isMultiLine={isMultiLine}
          isDarkMode={isDarkMode}
          isDisabled={warnIfNotSearchable && !isSearchable}
          aria-controls={open ? 'paper-chip-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          {...mkPaperChipTrackAttrs({ corpusId })}
        />
      </AnchorButton>
      <Menu
        id="paper-chip-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{
          'aria-labelledby': 'basic-button',
        }}
      >
        {!isMultiLine && paperTitle.length > 25 && (
          <div>
            <StyledDescription
              {...mkPaperMenuPaperTitleTrackAttrs({ corpusId })}
            >
              {paperTitle}
            </StyledDescription>
            <Divider />
          </div>
        )}
        {isSearchable && (
          <MenuItem
            onClick={handleOpenReader}
            {...mkPaperMenuOpenReaderBtnTrackAttrs({ corpusId })}
          >
            Read with Nora
          </MenuItem>
        )}
        <LinkedMenuItemText href={`https://semanticscholar.org/p/${corpusId}`}>
          <MenuItem
            onClick={handleClose}
            {...mkPaperMenuOpenS2BtnTrackAttrs({ corpusId })}
          >
            Open Paper Details Page
          </MenuItem>
        </LinkedMenuItemText>

        {!isReadOnly && noraExperienceVariant !== TABLE_ANNOTATION && (
          <div>
            <Divider />
            <MenuItem
              onClick={handleAskQuestion}
              {...mkPaperMenuPaperQABtnTrackAttrs({ corpusId })}
            >
              Ask a question about this paper
            </MenuItem>
            {!hideAddPaperToTable && (
              <MenuItem
                onClick={handleAddToTable}
                {...mkPaperMenuOpenLitReviewBtnTrackAttrs({ corpusId })}
              >
                Add to my literature review table
              </MenuItem>
            )}
          </div>
        )}
        {/* <MenuItem onClick={handleClose} disabled>
          Relate this paper with you
        </MenuItem>
        <MenuItem onClick={handleClose} disabled>
          Ideate with this paper
        </MenuItem> */}
      </Menu>
    </TitleChipContainer>
  );
};

const TitleChipContainer = styled('span')(
  ({
    isMultiLine,
    isFullWidth,
    hasMargin,
  }: {
    isMultiLine: boolean;
    isFullWidth: boolean;
    hasMargin: boolean;
  }) => ({
    display: 'inline-block',
    width: isMultiLine && !isFullWidth ? '260px' : 'unset',
    margin: hasMargin ? '4px' : undefined,
  }),
);

const WarningChip = styled('div')(
  ({ isDarkMode = false }: { isDarkMode?: boolean }) => ({
    width: 'max-content',
    background: isDarkMode ? 'rgb(10, 50, 53)' : 'white', // dark-teal
    borderRadius: '4px',
    padding: '3px 6px',
    fontSize: '12px',
    marginBottom: '4px',
  }),
);
